import React, { useEffect, useState, useRef } from 'react';
import api from '../api/client';
import logo from '../logo.png';

const POLL_INTERVAL = 2000; // ms — client-driven refresh, no reload needed by signage software
const POP_DURATION   = 900; // ms

const COLS       = ['B', 'I', 'N', 'G', 'O'];
const CELL_BG     = '#0f2137';
const CELL_DRAWN  = '#1e4976';
const CELL_LATEST = '#d97706';
const GRID_BG     = '#071220';

function columnOf(num) {
  return COLS[Math.ceil(num / 15) - 1];
}

export default function BingoShow() {
  const [drawn, setDrawn]         = useState([]);
  const [current, setCurrent]     = useState(null);
  const [remaining, setRemaining] = useState(75);
  const prevCurrent = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const load = () => {
      api.get('/api/bingo/game').then((r) => {
        if (cancelled) return;
        const { drawn: d, current: c, remaining: rem } = r.data;
        if (c !== null && c !== prevCurrent.current) {
          prevCurrent.current = c;
        }
        setDrawn(d || []);
        setCurrent(c ?? null);
        setRemaining(rem ?? 75);
      }).catch(() => {});
    };

    load();
    const interval = setInterval(load, POLL_INTERVAL);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const finished = remaining === 0 && drawn.length > 0;

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden select-none" style={{ background: GRID_BG }}>
      <style>{`
        @keyframes bingoPop {
          0%   { transform: scale(0.7); opacity: 0; }
          40%  { transform: scale(1.12); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        .bingo-pop { animation: bingoPop ${POP_DURATION}ms cubic-bezier(0.34, 1.56, 0.64, 1); }
      `}</style>

      {/* Header — sized off vh so it never eats too much space on short screens */}
      <div className="flex items-center flex-shrink-0" style={{ padding: 'clamp(6px, 1.5vh, 20px) clamp(12px, 3vw, 40px)' }}>
        <img src={logo} alt="BeachLAN" className="h-40 w-auto object-contain drop-shadow-lg opacity-90 flex-shrink-0" />
        <div className="flex-1 flex justify-center" style={{ paddingRight: '10rem' }}>
          <h1
            className="text-sand-300 font-black uppercase tracking-widest drop-shadow-lg text-center"
            style={{ fontSize: 'clamp(1rem, 3.2vh, 2.6rem)' }}
          >
            Bingo-Ziehung
          </h1>
        </div>
      </div>

      {/* Aktuelle Zahl */}
      <div className="flex-shrink-0 flex flex-col items-center justify-center" style={{ padding: 'clamp(4px, 1vh, 16px) 0' }}>
        {finished ? (
          <p
            className="text-sand-300 font-black text-center drop-shadow-2xl px-4"
            style={{ fontSize: 'clamp(1.4rem, 4.5vh, 3.2rem)' }}
          >
            🎉 Alle 75 Zahlen gezogen!
          </p>
        ) : current === null ? (
          <p className="text-white/30 tracking-widest uppercase" style={{ fontSize: 'clamp(0.9rem, 2.2vh, 1.5rem)' }}>
            Bereit für die erste Zahl…
          </p>
        ) : (
          <div
            key={current}
            className="bingo-pop flex flex-col items-center justify-center rounded-3xl shadow-2xl"
            style={{
              background: CELL_LATEST,
              width: 'clamp(90px, 20vh, 220px)',
              height: 'clamp(90px, 20vh, 220px)',
              boxShadow: '0 0 90px rgba(217,119,6,0.45)',
            }}
          >
            <span
              className="font-black leading-none"
              style={{ fontSize: 'clamp(0.9rem, 2.6vh, 1.9rem)', color: '#3a2205' }}
            >
              {columnOf(current)}
            </span>
            <span
              className="font-black leading-none tabular-nums"
              style={{ fontSize: 'clamp(2.4rem, 8vh, 6rem)', color: '#3a2205' }}
            >
              {current}
            </span>
          </div>
        )}

        <p className="text-ocean-300 tracking-wide tabular-nums" style={{ fontSize: 'clamp(0.75rem, 1.8vh, 1.2rem)', marginTop: 'clamp(6px, 1.5vh, 20px)' }}>
          {drawn.length} von 75 Zahlen gezogen
        </p>
        <div className="w-full max-w-xl h-1.5 bg-white/10 rounded-full overflow-hidden" style={{ marginTop: 'clamp(4px, 1vh, 8px)' }}>
          <div
            className="h-full bg-sand-400 rounded-full transition-all"
            style={{ width: `${(drawn.length / 75) * 100}%` }}
          />
        </div>
      </div>

      {/* Zahlenraster B I N G O — minmax(0, 1fr) statt 1fr, sonst können die Zeilen nicht unter ihre Inhaltsgröße schrumpfen und die letzten Reihen fallen aus dem sichtbaren Bereich */}
      <div className="flex-1 min-h-0" style={{ padding: '0 clamp(8px, 2vw, 48px) clamp(4px, 1vh, 16px)' }}>
        <div
          className="h-full grid grid-cols-5"
          style={{ gridTemplateRows: 'auto repeat(15, minmax(0, 1fr))', gap: 'clamp(2px, 0.4vh, 8px)' }}
        >
          {COLS.map((col) => (
            <div key={col} className="text-center font-black text-ocean-400 tracking-widest flex items-center justify-center"
              style={{ fontSize: 'clamp(0.7rem, 2vh, 1.3rem)' }}>
              {col}
            </div>
          ))}
          {Array.from({ length: 15 }, (_, row) =>
            COLS.map((_, colIdx) => {
              const num = colIdx * 15 + row + 1;
              const isDrawn  = drawn.includes(num);
              const isLatest = num === current;
              return (
                <div
                  key={num}
                  className="rounded-lg flex items-center justify-center font-bold tabular-nums transition-colors overflow-hidden"
                  style={{
                    background: isLatest ? CELL_LATEST : isDrawn ? CELL_DRAWN : CELL_BG,
                    color: isLatest ? '#3a2205' : isDrawn ? '#e0f0ff' : '#3a5570',
                    fontSize: 'clamp(0.55rem, 1.8vh, 1.15rem)',
                    border: isLatest ? '2px solid #fbbf24' : '1px solid rgba(255,255,255,0.04)',
                    minHeight: 0,
                    minWidth: 0,
                  }}
                >
                  {num}
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="flex-shrink-0 text-center text-white/20 tracking-widest uppercase" style={{ fontSize: 'clamp(0.5rem, 1.2vh, 0.7rem)', padding: '0 0 clamp(2px, 0.6vh, 8px)' }}>
        BeachLAN · aktualisiert automatisch
      </div>
    </div>
  );
}
